import { Controller } from "@hotwired/stimulus";
import { preloadEmojiImage } from "../helpers/emoji_image_cache";

let emojiMartPromise = null;
let emojiSpritePromise = null;

function preloadEmojiSprite() {
  if (!emojiSpritePromise) {
    emojiSpritePromise = new Promise((resolve) => {
      const image = new Image();

      image.onload = async () => {
        try {
          await image.decode();
        } catch (error) {
          console.warn("Emoji sprite decode failed", error);
        }

        resolve();
      };

      image.onerror = resolve;
      image.src = "https://emoji.itoons.com/emoji.png";
    });
  }

  return emojiSpritePromise;
}


function preloadEmojiMart() {
  if (!emojiMartPromise) {
    emojiMartPromise = Promise.all([
      import("../emoji_mart/emoji-mart-data"),
      import("../emoji_mart/emoji-mart"),
      preloadEmojiSprite()
    ]).catch((error) => {
      emojiMartPromise = null;
      throw error;
    });
  }

  return emojiMartPromise;
}

export default class EmojiMartController extends Controller {
  static targets = ["picker", "quillInput", "emojiToggle", "showEmoji", "closeButton", "emojiItem"];

  connect() {
    this.handlePickerClick = this.handlePickerClick.bind(this);
    if (this.hasEmojiToggleTarget) {
      this.toggleOwnerControls();
      this.emojiArray = [];
      if (this.hasShowEmojiTarget) {
        this.emojiArray = JSON.parse($(this.showEmojiTarget).attr("data-exist-emoji"));
      }
    }
    if (this.hasPickerTarget) {
      this.pickerTarget.addEventListener("click", this.handlePickerClick);
    }
  }

  disconnect() {
    this.removePickerPreloadListeners();
    this.stopVisibleEmojiPreloading();
    if (this.hasPickerTarget) {
      this.pickerTarget.removeEventListener("click", this.handlePickerClick);
    }
    globalThis.removeEventListener("scroll", this.onScroll, true);
    this.pickerInstance?.remove?.();
    this.pickerInstance = null;
    this.pickerReady = null;
    this.quillInstance = null;
  }

  toggleOwnerControls() {
    this.ownerId = Number.parseInt(this.element.dataset.ownerId);
    this.currentUserId = Current.user.id;
    const isOwner = this.ownerId === this.currentUserId;

    for (const item of this.emojiItemTargets) {
      if (isOwner) {
        item.classList.add("pe-none");
        delete item.dataset.action;
      } else {
        item.classList.remove("pe-none");
        item.dataset.action = "click->emoji-mart#removeEmoji";
      }
    }

    for (const element of this.element.querySelectorAll(".receiver-only")) {
      element.classList.toggle("d-none", isOwner);
    }
  }

  handlePickerClick(event) {
    event.stopPropagation();
  }

  get emojiApiUrl() {
    return this.element.dataset.emojiUrl;
  }

  get quill() {
    if (this.quillInstance) return this.quillInstance;
    if (!this.hasQuillInputTarget) return null;

    const controller = this.application.getControllerForElementAndIdentifier(
      this.quillInputTarget,
      "quill"
    );

    this.quillInstance = controller?.quill || null;
    return this.quillInstance;
  }

  getEmojiUrl(unified) {
    return `https://emoji.itoons.com/emojis/${String(unified).toLowerCase()}.png`;
  }

  ensurePicker() {
    if (this.pickerInstance) return Promise.resolve(this.pickerInstance);
    if (this.pickerReady) return this.pickerReady;

    this.pickerReady = this.setupPicker()
      .then(() => this.pickerInstance)
      .catch((error) => {
        this.pickerReady = null;
        console.error("Emoji picker failed to load", error);
        throw error;
      });

    return this.pickerReady;
  }

  async setupPicker() {
    if (!this.hasPickerTarget) return;
    const [dataModule, emojiMart] = await preloadEmojiMart();
    if (!this.element.isConnected || !this.hasPickerTarget) return;

    const data = dataModule.default;
    const { Picker } = emojiMart;

    // Prevent duplicate (Turbo fix)
    this.pickerTarget.innerHTML = "";

    this.pickerInstance = new Picker({
      data: data,
      getSpritesheetURL: () => "https://emoji.itoons.com/emoji.png",
      set: "facebook",
      native: false,
      sheetSize: 64,
      onEmojiSelect: (emoji) => this.handleEmojiSelect(emoji),
      perLine: 6,
      emojiSize: 30,
      emojiButtonSize: 46,
      previewPosition: "none",
      skinTonePosition: "none",
      navPosition: "top",
      theme: "none"
    });

    this.pickerTarget.appendChild(this.pickerInstance);
    this.addPickerTranslationStyle();
    this.addPickerPreloadListeners();
    this.addCloseButton();
  }

  addPickerTranslationStyle() {
    const root = this.pickerInstance?.shadowRoot;
    if (!root) return;

    const style = document.createElement("style");
    style.textContent = `
      .scroll {
        overscroll-behavior: contain;
      }

      .category > .sticky:has(> *) {
        font-size: 0;
      }

      .category > .sticky > * {
        font-size: 1rem;
      }
    `;
    root.appendChild(style);
  }

  addPickerPreloadListeners() {
    const root = this.pickerInstance?.shadowRoot;
    if (!root || this.pickerPreloadRoot === root) return;

    this.removePickerPreloadListeners();
    this.handlePickerIntent = this.handlePickerIntent.bind(this);
    this.pickerPreloadRoot = root;
    root.addEventListener("pointerdown", this.handlePickerIntent, true);
  }

  removePickerPreloadListeners() {
    if (!this.pickerPreloadRoot || !this.handlePickerIntent) return;

    this.pickerPreloadRoot.removeEventListener("pointerdown", this.handlePickerIntent, true);
    this.pickerPreloadRoot = null;
  }

  handlePickerIntent(event) {
    const button = event.target.closest?.("button[aria-label]");
    const unified = this.unifiedFromNative(button?.getAttribute("aria-label"));
    if (unified) preloadEmojiImage(this.getEmojiUrl(unified));
  }

  startVisibleEmojiPreloading() {
    if (this.visibleEmojiScrollContainer) return;

    const root = this.pickerInstance?.shadowRoot;
    const scrollContainer = root?.querySelector(".scroll");
    if (!root || !scrollContainer || !globalThis.MutationObserver) return;

    this.visibleEmojiQueue = [];
    this.visibleEmojiQueuedUrls ||= new Set();
    this.visibleEmojiActiveRequests ||= 0;
    this.visibleEmojiScrollContainer = scrollContainer;
    this.handleVisibleEmojiScroll = this.scheduleVisibleEmojiBatch.bind(this);
    scrollContainer.addEventListener("scroll", this.handleVisibleEmojiScroll, { passive: true });

    this.visibleEmojiMutationObserver = new globalThis.MutationObserver(() => {
      this.scheduleVisibleEmojiBatch();
    });
    this.visibleEmojiMutationObserver.observe(scrollContainer, {
      childList: true,
      subtree: true
    });

    this.scheduleVisibleEmojiBatch(0);
  }

  scheduleVisibleEmojiBatch(delay = 120) {
    this.clearQueuedVisibleEmojiImages();
    globalThis.clearTimeout(this.visibleEmojiBatchTimer);
    this.visibleEmojiBatchTimer = globalThis.setTimeout(() => {
      this.preloadVisibleEmojiBatch();
    }, delay);
  }

  preloadVisibleEmojiBatch(limit = 12) {
    const scrollContainer = this.visibleEmojiScrollContainer;
    if (!scrollContainer) return;

    const rootRect = scrollContainer.getBoundingClientRect();
    const margin = 46;
    const buttons = [...scrollContainer.querySelectorAll("button[aria-label]")]
      .map((button) => ({ button, rect: button.getBoundingClientRect() }))
      .filter(({ button, rect }) => {
        return this.unifiedFromNative(button.getAttribute("aria-label")) &&
          rect.bottom >= rootRect.top - margin &&
          rect.top <= rootRect.bottom + margin;
      })
      .sort((left, right) => {
        return Math.abs(left.rect.top - rootRect.top) - Math.abs(right.rect.top - rootRect.top);
      })
      .slice(0, limit);

    for (const { button } of buttons) {
      const unified = this.unifiedFromNative(button.getAttribute("aria-label"));
      this.enqueueVisibleEmojiImage(this.getEmojiUrl(unified));
    }
  }

  clearQueuedVisibleEmojiImages() {
    for (const url of this.visibleEmojiQueue || []) {
      this.visibleEmojiQueuedUrls?.delete(url);
    }

    this.visibleEmojiQueue = [];
  }

  enqueueVisibleEmojiImage(url) {
    if (this.visibleEmojiQueuedUrls.has(url)) return;

    this.visibleEmojiQueuedUrls.add(url);
    this.visibleEmojiQueue.push(url);
    this.preloadNextVisibleEmojiImages();
  }

  preloadNextVisibleEmojiImages() {
    while (this.visibleEmojiActiveRequests < 2 && this.visibleEmojiQueue.length > 0) {
      const url = this.visibleEmojiQueue.shift();
      this.visibleEmojiActiveRequests += 1;

      preloadEmojiImage(url).finally(() => {
        this.visibleEmojiActiveRequests -= 1;
        this.visibleEmojiQueuedUrls.delete(url);
        this.preloadNextVisibleEmojiImages();
      });
    }
  }

  stopVisibleEmojiPreloading() {
    globalThis.clearTimeout(this.visibleEmojiBatchTimer);
    this.visibleEmojiScrollContainer?.removeEventListener("scroll", this.handleVisibleEmojiScroll);
    this.visibleEmojiMutationObserver?.disconnect();
    this.visibleEmojiBatchTimer = null;
    this.visibleEmojiScrollContainer = null;
    this.visibleEmojiMutationObserver = null;
    this.clearQueuedVisibleEmojiImages();
  }

  unifiedFromNative(nativeEmoji) {
    if (!nativeEmoji || !/\p{Extended_Pictographic}/u.test(nativeEmoji)) return null;

    return Array.from(nativeEmoji)
      .map((character) => character.codePointAt(0).toString(16))
      .join("-");
  }

  // ✅ Handle emoji select
  async handleEmojiSelect(emoji) {
    if (this.quill) {
      await preloadEmojiImage(this.getEmojiUrl(emoji.unified));
      if (!this.element.isConnected) return;
      this.insertIntoQuill(emoji);
    } else if (this.hasEmojiToggleTarget) {
      const unified = emoji.unified;

      // if emoji already exists -> remove it
      const existingIndex = this.emojiArray.indexOf(unified);

      if (existingIndex === -1) {
        this.emojiArray.push(unified); // add new emoji
      } else {
        this.emojiArray.splice(existingIndex, 1); // remove existing emoji
      }

      postEmojiData(this.emojiArray, $(this.element).attr("data-msg-id"));
    }

    this.element.dispatchEvent(
      new CustomEvent("emoji-mart:select", {
        bubbles: true,
        detail: {
          unified: emoji.unified,
          url: this.getEmojiUrl(emoji.unified),
          native: emoji.native
        }
      })
    );
  }

  insertIntoQuill(emoji) {
    const quill = this.quill;
    if (!quill) return;

    quill.focus();

    const range = quill.getSelection(true);
    const index = range ? range.index : quill.getLength();

    quill.insertEmbed(
      index,
      "emoji",
      {
        unified: emoji.unified,
        url: this.getEmojiUrl(emoji.unified),
        native: emoji.native
      },
      "user"
    );

    quill.setSelection(index + 1, 0, "user");
    quill.root.dispatchEvent(new Event("input", { bubbles: true }));

  }

  removeEmoji(e) {
    e.preventDefault();

    const emojiIndex = this.emojiArray.indexOf(e.currentTarget.dataset.emoji);
    if (emojiIndex === -1) return;

    this.emojiArray.splice(emojiIndex, 1);
    postEmojiData(this.emojiArray, $(this.element).attr("data-msg-id"));

    const reactionItem = e.currentTarget.closest("li") || e.currentTarget;
    reactionItem.remove();
  }

  addCloseButton() {
    requestAnimationFrame(() => {
      const root = this.pickerInstance?.shadowRoot?.querySelector("#root");
      if (!root || !this.hasCloseButtonTarget) return;
      const button = this.closeButtonTarget;
      if (!root || !button) return;

      button.onclick = (event) => {
        event.preventDefault();
        event.stopPropagation();
        button.dispatchEvent(
          new CustomEvent("emoji-mart-dropdown:hide", {
            bubbles: true,
            composed: true
          })
        );
      };

      if (root.contains(button)) return;

      root.prepend(button);
    });
  }
}

async function postEmojiData(emoji_array, msg_id) {
  const turboRequest = (await import("../helpers/api")).turboRequest;

  await turboRequest("/messages/" + msg_id + "/save_emoji", {
    method: "POST",
    params: { emoji_array: emoji_array },
    turboStream: false
  });
}
